// SnackbarContext.tsx
import {
  CheckCircleSharp,
  ErrorSharp,
  InfoSharp,
  WarningOutlined,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import Slide, { SlideProps } from "@mui/material/Slide";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type SnackbarContextType = {
  showSnackbar: (
    message: string,
    severity?: "success" | "error" | "warning" | "info",
  ) => void;
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  // Stable callback identity so consumers using showSnackbar in useCallback /
  // useEffect deps don't trigger re-runs on every provider render.
  const showSnackbar = useCallback(
    (
      msg: string,
      type: "success" | "error" | "warning" | "info" = "success",
    ) => {
      setMessage(msg);
      setSeverity(type);
      setOpen(true);
    },
    [],
  );

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // Memoize the context value so its reference is stable across re-renders.
  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        // TransitionComponent={SlideTransition}
        slots={{ transition: SlideTransition }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
          iconMapping={{
            success: <CheckCircleSharp fontSize="inherit" />,
            error: <ErrorSharp fontSize="inherit" />,
            info: <InfoSharp fontSize="inherit" />,
            warning: <WarningOutlined fontSize="inherit" />,
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
